/**
 * /for/* registry — industry/role-bundled marketing surfaces.
 *
 * W.4.19 Bucket C. Each entry references doc-type and integration slugs to
 * compose a vertical narrative without duplicating content.
 */

export interface IndustryWorkflow {
  title: string;
  steps: string[];
}

export interface IndustryMeta {
  slug: string;
  name: string;
  tagline: string;
  iconName: 'Calculator' | 'Truck' | 'Scale' | 'Home';
  metaTitle: string;
  metaDescription: string;
  h1: string;
  subhead: string;
  /** doc-type slugs that this industry uses. */
  docTypeSlugs: string[];
  /** integration slugs relevant to this industry. */
  integrationSlugs: string[];
  workflows: IndustryWorkflow[];
}

export const INDUSTRIES: IndustryMeta[] = [
  {
    slug: 'accounting',
    name: 'Accounting & bookkeeping',
    tagline: 'Invoices, receipts, statements — all extracted, all tagged.',
    iconName: 'Calculator',
    metaTitle: 'okraPDF for Accounting — invoice, receipt, statement extraction',
    metaDescription:
      'Automate AP, expense reporting, and reconciliation with PDF extraction for invoices, receipts, bank statements, and W-9s.',
    h1: 'okraPDF for accounting & bookkeeping',
    subhead:
      'Stop typing invoice totals into Sheets. PDFs in, typed rows out, your existing GL untouched.',
    docTypeSlugs: ['invoice', 'receipt', 'bank-statement', 'utility-bill', 'w9'],
    integrationSlugs: ['google-sheets', 'zapier', 'webhooks'],
    workflows: [
      {
        title: 'AP automation',
        steps: [
          'Vendor emails an invoice PDF',
          'okraPDF extracts vendor, totals, line items, due date',
          'Row drops into your AP queue or directly to QuickBooks via Zapier',
          'Approver reviews exceptions only — clean rows pass through',
        ],
      },
      {
        title: 'Expense reporting',
        steps: [
          'Employee snaps a receipt photo',
          'okraPDF extracts merchant, items, total, tax',
          'Row lands in the expense report Sheet with category pre-filled',
        ],
      },
      {
        title: 'Bank reconciliation',
        steps: [
          'Drop bank statement PDFs in a watched Drive folder',
          'okraPDF extracts every transaction with running balance',
          'Match against GL entries — exceptions flagged for review',
        ],
      },
    ],
  },
  {
    slug: 'logistics',
    name: 'Logistics & shipping',
    tagline: 'BoLs, packing slips, POs — extracted, tracked, paid on time.',
    iconName: 'Truck',
    metaTitle: 'okraPDF for Logistics — BoL, packing slip, PO extraction',
    metaDescription:
      'Automate freight admin with PDF extraction for bills of lading, packing slips, purchase orders, and invoices. OCR-on-scan included.',
    h1: 'okraPDF for logistics & shipping',
    subhead:
      'Faxed BoLs, scanned packing slips, vendor POs — pull every shipment into typed rows so dispatch and AP catch every exception.',
    docTypeSlugs: ['bill-of-lading', 'packing-slip', 'purchase-order', 'invoice'],
    integrationSlugs: ['google-drive', 'zapier', 'webhooks'],
    workflows: [
      {
        title: 'Freight billing reconciliation',
        steps: [
          'Carrier sends BoL + invoice PDF',
          'okraPDF extracts shipment details and freight charges',
          'Row matched against rate sheet — discrepancies flagged for AP',
        ],
      },
      {
        title: 'Packing slip vs PO matching',
        steps: [
          'Receiving scans packing slip on dock',
          'okraPDF extracts SKU + quantities shipped',
          'Match against PO — partial shipments and substitutions raised in real time',
        ],
      },
      {
        title: 'Multi-carrier tracking ingest',
        steps: [
          'BoLs from any carrier drop into a watched folder',
          'okraPDF extracts tracking + carrier SCAC',
          'Tracking numbers feed your TMS or shipment dashboard',
        ],
      },
    ],
  },
  {
    slug: 'legal',
    name: 'Legal services',
    tagline: 'Contracts, court filings, redactions — searchable, citable, safe.',
    iconName: 'Scale',
    metaTitle: 'okraPDF for Legal — contract extraction, redaction, citations',
    metaDescription:
      'Pull data from contracts and filings, redact PII before share, and link extracted values back to the source page and bbox.',
    h1: 'okraPDF for legal services',
    subhead:
      'Extract counterparties, dates, and clauses from any contract PDF. Redact before sharing. Every value cites its source page.',
    docTypeSlugs: ['invoice', 'w9', 'insurance-claim'],
    integrationSlugs: ['google-drive', 'dropbox', 'webhooks'],
    workflows: [
      {
        title: 'Contract abstraction',
        steps: [
          'Counsel uploads a contract PDF',
          'okraPDF extracts parties, effective date, term, payment terms',
          'Each field links back to the source page + bbox for review',
        ],
      },
      {
        title: 'Redaction before share',
        steps: [
          'PDF flagged for outside-counsel review',
          'okraPDF redacts PII per plain-English instructions',
          'Redacted PDF is shared; redaction log is retained for audit',
        ],
      },
      {
        title: 'Discovery indexing',
        steps: [
          'Bulk PDFs land in a watched Drive folder',
          'okraPDF extracts dates, parties, doc type',
          'Index lands in your DMS or e-discovery tool',
        ],
      },
    ],
  },
  {
    slug: 'real-estate',
    name: 'Real estate & title',
    tagline: 'Closing docs, insurance, title work — typed, indexed, archived.',
    iconName: 'Home',
    metaTitle: 'okraPDF for Real Estate — closing docs, insurance extraction',
    metaDescription:
      'Extract data from closing packages, ACORD insurance, and utility bills for KYC. Watch a Dropbox folder, get typed rows.',
    h1: 'okraPDF for real estate & title',
    subhead:
      'Closing docs, ACORD certs, utility bills for proof-of-address — every property file pulled into typed rows for the file, the underwriter, and the buyer.',
    docTypeSlugs: ['insurance-claim', 'utility-bill', 'w9', 'invoice'],
    integrationSlugs: ['dropbox', 'webhooks', 'google-drive'],
    workflows: [
      {
        title: 'Closing package abstraction',
        steps: [
          'Title agent uploads the closing package PDF',
          'okraPDF extracts party names, parcel ID, sale price, dates',
          'Row populates the closing file in your TMS',
        ],
      },
      {
        title: 'KYC address verification',
        steps: [
          'Buyer uploads utility bill for proof of address',
          'okraPDF extracts service address + service period',
          'Match against application — flag mismatches for compliance review',
        ],
      },
      {
        title: 'ACORD intake for insurance binding',
        steps: [
          'Producer emails ACORD 25 PDF',
          'okraPDF extracts insured, coverage limits, expiration',
          'Row drops into the binder workflow',
        ],
      },
    ],
  },
];

export const INDUSTRIES_BY_SLUG: Record<string, IndustryMeta> = INDUSTRIES.reduce(
  (acc, i) => {
    acc[i.slug] = i;
    return acc;
  },
  {} as Record<string, IndustryMeta>,
);

export const INDUSTRIES_INDEX_SEO = {
  title: 'okraPDF for industries — PDF extraction workflows by role',
  description:
    'Browse okraPDF workflows for accounting, logistics, legal, and real estate teams. Extract structured rows from the PDFs each team handles every day.',
  canonical: 'https://okrapdf.com/for',
  h1: 'okraPDF for your industry.',
  subhead:
    'Pick your industry. See the typical PDFs you process, the integrations that wire them up, and the workflows other teams have already built.',
};

export function industrySeoFor(slug: string): {
  title: string;
  description: string;
  canonical: string;
} | null {
  const i = INDUSTRIES_BY_SLUG[slug];
  if (!i) return null;
  return {
    title: i.metaTitle,
    description: i.metaDescription,
    canonical: `https://okrapdf.com/for/${i.slug}`,
  };
}
